/**
 * Working, local-tax wording and export rows for the State Tax Calculator (B-0124). Pure, so the site and the API
 * (/v1/converters/state-tax, synced by scripts/sync-core.mjs) build the same text from the same numbers.
 * The state-level maths is NOT here: it stays in stateTaxData.ts, unchanged.
 */
import type { CalcStep } from "@/lib/calc/steps";
import { STATE_BY_CODE, stateTax, type StateFiling, type StateInfo } from "./stateTaxData";

export interface LocalNotice {
  /** "none": the state has no local income tax to mention. "included": the box is ticked. "excluded": a warning is due. */
  status: "none" | "included" | "excluded";
  /** Average local rate in percent, when the table has one. */
  avg?: number;
  text: string;
}

/**
 * The line shown next to the result. Warns even when the box is off. States with local taxes but no usable average
 * (Michigan) can only warn: the box is not offered because there is no figure to apply.
 */
export function localNotice(state: StateInfo | undefined, included: boolean): LocalNotice {
  if (!state || state.kind === "none" || !state.localNote) return { status: "none", text: "" };
  if (!state.localAvg) return { status: "excluded", text: `Local income tax is not included. ${state.localNote} There is no average to apply, so add your city's tax yourself.` };
  if (included) return { status: "included", avg: state.localAvg, text: `Includes an estimated local income tax of ${state.localAvg}% of state taxable income (a typical average; your city or county may be higher or lower). ${state.localNote}` };
  return { status: "excluded", avg: state.localAvg, text: `State tax only: local income tax is not included, so your total is likely higher. ${state.localNote} Tick the local-tax box to add a typical ${state.localAvg}%.` };
}

/** Short label for the Compare table: whether that row includes local tax. */
export function localCompareLabel(state: StateInfo, included: boolean): string {
  if (state.kind === "none" || !state.localNote) return "none";
  if (!state.localAvg) return "no average";
  return included ? `included ${state.localAvg}%` : "not included";
}

/** One sentence above the Compare table. */
export function compareLocalCaption(included: boolean): string {
  return included
    ? "Includes an average local income tax for the states that have one (IN, MD, NY, OH, PA). Other local taxes (such as Michigan cities) are not included."
    : "State tax only: local income taxes are not included in any row. Tick the local-tax box to add an average for IN, MD, NY, OH and PA.";
}

export interface StateTaxLine { label: string; from: number | null; to: number | null; ratePct: number | null; amount: number }

/** Rows of the breakdown table and the CSV: every bracket, the local tax if added, then the total. */
export function stateTaxLines(code: string, income: number, filing: StateFiling, fedStd: number, local: boolean): StateTaxLine[] {
  const s = STATE_BY_CODE[code];
  const r = stateTax(code, income, filing, fedStd, { local });
  if (!s || s.kind === "none") return [{ label: "Total estimated state income tax", from: null, to: null, ratePct: null, amount: 0 }];
  const lines: StateTaxLine[] = r.rows.map((b) => ({ label: "Bracket", from: b.from, to: b.to, ratePct: b.rate, amount: b.tax }));
  const bracketSum = r.rows.reduce((a, b) => a + b.tax, 0);
  if (Math.abs(bracketSum - r.stateOnly) > 0.005) lines.push({ label: "Credits (personal exemption credit)", from: null, to: null, ratePct: null, amount: r.stateOnly - bracketSum });
  if (r.local > 0) lines.push({ label: `Local income tax (estimate, ${r.localRate}% average)`, from: null, to: null, ratePct: r.localRate, amount: r.local });
  lines.push({ label: "Total estimated state income tax", from: null, to: null, ratePct: null, amount: r.tax });
  return lines;
}

/** The working behind the result, from the same stateTax() call the page shows. */
export function stateTaxSteps(code: string, income: number, filing: StateFiling, fedStd: number, local: boolean, fmt: (n: number) => string | number): CalcStep[] {
  const s = STATE_BY_CODE[code];
  if (!s) return [];
  const r = stateTax(code, income, filing, fedStd, { local });
  if (s.kind === "none") return [{ label: "No state income tax", formula: `${s.name} does not tax wages`, inputs: { Income: fmt(income) }, result: fmt(0) }];
  const steps: CalcStep[] = [
    { label: "Taxable income", formula: "Income - state standard deduction - personal exemptions (never below 0)", inputs: { Income: fmt(income), "Deduction and exemptions": fmt(Math.min(income, income - r.taxable)) }, result: fmt(r.taxable) },
    { label: "Tax by bracket", formula: "For each bracket: taxable income inside the bracket x its rate; add the brackets (less any credit)", inputs: Object.fromEntries(r.rows.map((b, i) => [`Bracket ${i + 1} (${b.rate}%)`, fmt(b.tax)])), result: fmt(r.stateOnly) },
  ];
  if (r.local > 0) {
    steps.push({ label: "Local income tax (estimate)", formula: "State taxable income x typical local average rate", inputs: { "State taxable income": fmt(r.taxable), "Local average": `${r.localRate}%` }, result: fmt(r.local) });
    steps.push({ label: "Total", formula: "State tax + local tax", inputs: { "State tax": fmt(r.stateOnly), "Local tax": fmt(r.local) }, result: fmt(r.tax) });
  } else {
    steps.push({ label: "Total", formula: "State tax (local income tax not included)", inputs: { "State tax": fmt(r.stateOnly) }, result: fmt(r.tax) });
  }
  return steps;
}
